import React, { useState, useMemo, useEffect } from 'react';

import { Segmented } from 'antd';
import { isEmpty } from 'lodash';

import { getMoment } from '@libs/NwMoment';

import { useContactBillingInfos } from '@hooks/API/Contact/UseContactBillingInfos';
import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

import { useStatementContext, useStatementDispatch } from '../StatementContext';
import { isStatementFiltered } from '../StatementFilters';
import { StatementContainer } from '../StatementUI';
import { getFilteredStatementTotals, getPeriodDelta } from '../StatementUtils';
import PayableBalances from './PayableBalances';
import PayableBody from './PayableBody';
import PayableFilters from './PayableFilters';
import { filterTransactions } from './PayableHelpers';
import PayableNavigation from './PayableNavigation';
import PayablePrintable from './PayablePrintable';
import PayableTools from './PayableTools';

/**
 * PayableWrapper
 *
 * Main wrapper component for payable statements.
 * Handles filtering, balances display, and orchestrates sub-components.
 */
const PayableWrapper = ({ payments, statement, isLoading }) => {
    const { contactID, contactType, statementType, loading, filters, alwaysEnabled, expand } = useStatementContext();

    const { data: billingInfos, isLoading: billingInfosLoading } = useContactBillingInfos(contactID);

    const multipleBillingInfos = useMemo(() => {
        if (billingInfos) {
            return billingInfos.length > 1;
        }
        return false;
    }, [billingInfos]);

    // Generate colors for billing infos
    const getColorForBillingInfo = (id, index) => {
        const palette = [
            '#FF6B6B',
            '#6BCB77',
            '#4D96FF',
            '#FFD93D',
            '#FF6F91',
            '#845EC2',
            '#00C9A7',
            '#F9F871',
            '#F98404',
            '#43BCCD',
        ];
        return palette[index % palette.length];
    };

    const billingInfoColorMap = useMemo(() => {
        if (!billingInfos) return {};
        const map = {};
        billingInfos.forEach((billingInfo, idx) => {
            map[billingInfo.ID] = getColorForBillingInfo(billingInfo.ID, idx);
        });
        return map;
    }, [billingInfos]);

    const billingInfoOptions = useMemo(() => {
        const options = [];
        if (billingInfos) {
            options.push({
                value: 'All',
                label: 'All Billing Profiles',
                'data-label': 'All Billing Profiles',
            });
            billingInfos.forEach((billingInfo) => {
                const color = billingInfoColorMap[billingInfo.ID];
                options.push({
                    value: billingInfo.ID,
                    label: (
                        <span className="billing-info-wrapper">
                            <span className="billing-info-dot" style={{ backgroundColor: color }} />
                            <span className="billing-info-texts">
                                <em>{billingInfo.BillingCode}</em>
                                <strong>
                                    {billingInfo.CompanyName} ({billingInfo.InvoiceType})
                                </strong>
                            </span>
                        </span>
                    ),
                    'data-label': billingInfo.BillingCode,
                });
            });
        }
        return options;
    }, [billingInfos, billingInfoColorMap]);

    const dispatch = useStatementDispatch();

    const onUpdateRange = (range) => {
        dispatch({ type: 'UpdateRange', value: range });
    };

    const onUpdatePeriod = (period) => {
        dispatch({ type: 'UpdateFilters', name: 'period', value: period });
    };

    const onUpdateExpand = (expand) => {
        dispatch({ type: 'UpdateExpand', value: expand });
    };

    const onUpdateClientStatus = (clientStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'clientStatus', value: clientStatus });
    };

    const onUpdatePaymentStatus = (paymentStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'paymentStatus', value: paymentStatus });
    };

    const onUpdateInvoiceStatus = (invoiceStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'invoiceStatus', value: invoiceStatus });
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [statementTypeFilter, setStatementTypeFilter] = useState('Real');
    const [previsionalStatusFilter, setPrevisionalStatusFilter] = useState('Billable');
    const [familiesFilter, setFamiliesFilter] = useState('All');
    // filters.modelFilter arriva dai filterOverrides: preseleziona la modella all'apertura
    const [modelFilter, setModelFilter] = useState(filters.modelFilter || null);
    const [modelList, setModelList] = useState([]);
    const [billingInfoFilter, setBillingInfoFilter] = useState('All');
    const [transactionOptions, setTransactionOptions] = useState([]);
    const [printDrawerVisible, setPrintDrawerVisible] = useState(false);

    const handleChangeSearchTerm = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearchTerm = () => {
        setSearchTerm('');
    };

    const configuration = useConfigurations();

    const onHoldBalances = useMemo(() => {
        if (statement && statement.onHoldBalances) {
            const hasNonZeroBalance = Object.values(statement.onHoldBalances).some((balance) => balance !== 0);
            if (hasNonZeroBalance) {
                return statement.onHoldBalances;
            }
            return null;
        }
        return null;
    }, [statement]);

    const availableBalances = useMemo(() => {
        if (statement && statement.availableBalances) {
            return statement.availableBalances;
        }
        return { [configuration.defaultCurrency]: 0 };
    }, [statement]);

    const totalBalances = useMemo(() => {
        if (statement && statement.totalBalances) {
            return statement.totalBalances;
        }
        return { [configuration.defaultCurrency]: 0 };
    }, [statement]);

    const startBalances = useMemo(() => {
        if (statement && statement.startBalances && !isEmpty(statement.startBalances)) {
            const result = {
                Balances: {},
                AvailableBalances: {},
            };
            for (const [currency, values] of Object.entries(statement.startBalances)) {
                result.Balances[currency] = values.Balance;
                result.AvailableBalances[currency] = values.AvailableBalance;
            }
            return result;
        }
        return null;
    }, [statement]);

    const endBalances = useMemo(() => {
        if (statement && statement.endBalances && !isEmpty(statement.endBalances)) {
            const result = {
                Balances: {},
                AvailableBalances: {},
            };
            for (const [currency, values] of Object.entries(statement.endBalances)) {
                result.Balances[currency] = values.Balance;
                result.AvailableBalances[currency] = values.AvailableBalance;
            }
            return result;
        }
        return null;
    }, [statement]);

    const handleOpenPrintDrawer = () => {
        setPrintDrawerVisible(true);
    };

    const handleClosePrintDrawer = () => {
        setPrintDrawerVisible(false);
    };

    const transactionsWithPayments = useMemo(() => {
        let transactions = [];
        let paymentsForStatement = [];
        if (statement && statement.Transactions && statement.Transactions.length > 0) {
            transactions = statement.Transactions;
        }
        if (payments && payments.length > 0 && filters.paymentStatus !== 'NotPaid') {
            paymentsForStatement = payments;
        }
        if (paymentsForStatement.length === 0 && transactions.length === 0) {
            return [];
        }
        // const rangeStart = getMoment(filters.range[0]);
        // const rangeEnd = getMoment(filters.range[1]);
        // const filteredPayments = payments.filter((p) => getMoment(p.Date).isBetween(rangeStart, rangeEnd, 'day', '[]'));
        const transWithPaym = [
            ...transactions.map((t) => ({
                ...t,
                isPayment: false,
            })),
            ...paymentsForStatement.map((p) => ({
                isPayment: true,
                ID: p.ID,
                PaymentDate: p.Date,
                PaymentName: p.Name,
                PaymentNumber: p.Number,
                PaymentAmount: p.TotalAmount,
                PaymentCurrency: p.Currency,
                RefDate: p.Date,
                Family: 'Payment',
            })),
        ];
        return transWithPaym.sort((a, b) => {
            return getMoment(a.RefDate).diff(getMoment(b.RefDate));
        });
    }, [statement, payments, filters.range, filters.paymentStatus]);

    const transactionTypesDefault = [
        { value: 'All', label: 'All row types', 'data-label': 'All row types' },
        { value: 'JobFee||CreditNoteRow', label: 'Job Fees', 'data-label': 'Job Fees' },
        { value: 'Expense', label: 'Expenses', 'data-label': 'Expenses' },
        { value: 'JobRefund', label: 'Job Refunds', 'data-label': 'Job Refunds' },
        { value: 'AdvPayment', label: 'Adv Payments', 'data-label': 'Adv Payments' },
        { value: 'Credit||Debit||Refund', label: 'Others', 'data-label': 'Others' },
        { value: 'Payment', label: 'Payments', 'data-label': 'Payments' },
    ];

    // Generate model list for agency and service contacts
    useEffect(() => {
        if (
            (contactType === 'agency' || contactType === 'service') &&
            statement &&
            statement.Transactions &&
            statement.Transactions.length > 0
        ) {
            const modelListFromTransactions = Array.from(
                new Set(statement.Transactions.filter((t) => t.ModelID).map((t) => t.ModelID))
            ).map((id) => ({
                value: id,
                label: getContactName(id),
            }));
            setModelList(modelListFromTransactions);
        }
    }, [statement, contactType]);

    const activeTransactionsWithPayments = useMemo(() => {
        let filteredTransactions = [];
        if (transactionsWithPayments && transactionsWithPayments.length > 0) {
            filteredTransactions = transactionsWithPayments;
            const InvoicedStatuses = ['Locked', 'Billed', 'Paid'];
            const AllPrevisionalStatuses = ['Billable', 'Job', 'Option'];
            const PrevisionalConfirmedStatuses = ['Job', 'Billable'];

            if (statementTypeFilter === 'Previsional') {
                filteredTransactions = filteredTransactions.filter(
                    (t) => !t.isPayment && !t.Statements.some((st) => st.OnHold)
                );
                if (previsionalStatusFilter !== 'All') {
                    switch (previsionalStatusFilter) {
                        case 'Billable':
                            filteredTransactions = filteredTransactions.filter(
                                (t) => t.ClientID && t.ClientInvoiceStatus === 'Billable'
                            );
                            break;
                        case 'Job':
                            filteredTransactions = filteredTransactions.filter(
                                (t) => t.ClientID && PrevisionalConfirmedStatuses.includes(t.ClientInvoiceStatus)
                            );
                            break;
                        default:
                            break;
                    }
                } else {
                    filteredTransactions = filteredTransactions.filter(
                        (t) => t.ClientID && AllPrevisionalStatuses.includes(t.ClientInvoiceStatus)
                    );
                }
                if (billingInfoFilter && billingInfoFilter !== 'All') {
                    filteredTransactions = filteredTransactions.filter(
                        (t) => t.Statements && t.Statements.some((st) => st.BillingInfoID === billingInfoFilter)
                    );
                }
            } else {
                if (statementTypeFilter === 'Hold') {
                    filteredTransactions = filteredTransactions.filter(
                        (t) => !t.isPayment && t.Statements.some((st) => st.OnHold)
                    );
                } else {
                    filteredTransactions = filteredTransactions.filter(
                        (t) => t.isPayment || !t.Statements.some((st) => st.OnHold)
                    );
                    if (billingInfoFilter && billingInfoFilter !== 'All') {
                        filteredTransactions = filteredTransactions.filter(
                            (t) => t.Statements && t.Statements.some((st) => st.BillingInfoID === billingInfoFilter)
                        );
                    }
                    if (filters.clientStatus !== 'All') {
                        switch (filters.clientStatus) {
                            case 'Invoiced':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.ClientID || InvoicedStatuses.includes(t.ClientInvoiceStatus)
                                );
                                break;
                            case 'NotPaid':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) =>
                                        t.ClientID &&
                                        (t.ClientInvoiceStatus === 'Locked' || t.ClientInvoiceStatus === 'Billed')
                                );
                                break;
                            case 'Paid':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.ClientID || t.ClientInvoiceStatus === 'Paid'
                                );
                                break;
                            default:
                                break;
                        }
                    }
                    if (filters.paymentStatus !== 'All') {
                        switch (filters.paymentStatus) {
                            case 'NotPaid':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.isPayment && t.Statements.some((st) => st.PaidStatus !== 'Paid')
                                );
                                break;
                            case 'Paid':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.isPayment && t.Statements.every((st) => st.PaidStatus === 'Paid')
                                );
                                break;
                            default:
                                break;
                        }
                    }
                    // Invoice status filter - only for models
                    if (contactType === 'model' && filters.invoiceStatus !== 'All') {
                        switch (filters.invoiceStatus) {
                            case 'NotInvoiced':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.isPayment && t.Statements.every((st) => !st.InvoiceID)
                                );
                                break;
                            case 'Invoiced':
                                filteredTransactions = filteredTransactions.filter(
                                    (t) => !t.isPayment && t.Statements.some((st) => st.InvoiceID)
                                );
                                break;
                            default:
                                break;
                        }
                    }
                }
            }

            const transactionTypes = [...transactionTypesDefault];
            transactionTypes.forEach((tt) => {
                if (tt.value === 'All') {
                    tt.label = `${tt['data-label']} [${filteredTransactions.length}]`;
                } else {
                    tt.label = `${tt['data-label']} [${filteredTransactions.filter((t) => tt.value.includes(t.Family)).length}]`;
                }
            });
            setTransactionOptions([...transactionTypes]);

            // Apply model filter for agency and service
            if ((contactType === 'agency' || contactType === 'service') && modelFilter) {
                filteredTransactions = filteredTransactions.filter((t) => t.ModelID === modelFilter);
            }

            // Apply families filter for model contact type
            if (contactType === 'model' && familiesFilter && familiesFilter !== 'All') {
                const families = familiesFilter.split('||');
                filteredTransactions = filteredTransactions.filter((t) => families.some((f) => t.Family === f));
            }

            if (searchTerm) {
                filteredTransactions = filterTransactions(filteredTransactions, searchTerm);
            }
        }
        return filteredTransactions;
    }, [
        transactionsWithPayments,
        searchTerm,
        filters,
        previsionalStatusFilter,
        statementTypeFilter,
        familiesFilter,
        modelFilter,
        contactType,
        billingInfoFilter,
    ]);

    const filtersToCheck = useMemo(() => {
        return {
            ...filters,
            previsionalStatusFilter,
            statementTypeFilter,
            familiesFilter,
            billingInfoFilter,
            searchTerm,
        };
    }, [filters, previsionalStatusFilter, statementTypeFilter, familiesFilter, billingInfoFilter, searchTerm]);

    const StatementTotals = useMemo(() => {
        const typeLabel = contactType === 'model' ? 'Model' : contactType === 'agency' ? 'Agency' : 'Service';
        let totals = [];
        const isFiltered = isStatementFiltered(filtersToCheck);
        if (isFiltered) {
            totals = getFilteredStatementTotals(
                activeTransactionsWithPayments,
                configuration.defaultCurrency,
                typeLabel
            );
        } else {
            totals = getPeriodDelta(startBalances, endBalances, configuration.defaultCurrency, false);
        }
        return totals;
    }, [activeTransactionsWithPayments, contactType, filtersToCheck, startBalances, endBalances]);

    if (isLoading && !statement) {
        return (
            <StatementContainer>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            </StatementContainer>
        );
    }

    return (
        <StatementContainer>
            {statement && (
                <div className="statement-total-box-wrapper">
                    {statementTypeFilter === 'Real' && (
                        <PayableBalances
                            onHoldBalances={onHoldBalances}
                            totalBalances={totalBalances}
                            availableBalances={availableBalances}
                        />
                    )}
                    {statementTypeFilter === 'Previsional' && (
                        <div className="statement-filter">
                            <small>job status</small>
                            <Segmented
                                value={previsionalStatusFilter}
                                onChange={setPrevisionalStatusFilter}
                                size="small"
                                options={[
                                    { value: 'All', label: 'All (Options/Jobs)' },
                                    { value: 'Job', label: 'Confirmed Job' },
                                    { value: 'Billable', label: 'To Bill' },
                                ]}
                            />
                        </div>
                    )}
                </div>
            )}
            <PayableNavigation
                billingInfoFilter={billingInfoFilter}
                billingInfoOptions={billingInfoOptions}
                endBalances={endBalances}
                familiesFilter={familiesFilter}
                handleChangeSearchTerm={handleChangeSearchTerm}
                handleClearSearchTerm={handleClearSearchTerm}
                modelFilter={modelFilter}
                modelList={modelList}
                multipleBillingInfos={multipleBillingInfos}
                onUpdatePeriod={onUpdatePeriod}
                onUpdateRange={onUpdateRange}
                searchTerm={searchTerm}
                setBillingInfoFilter={setBillingInfoFilter}
                setFamiliesFilter={setFamiliesFilter}
                setModelFilter={setModelFilter}
                setStatementTypeFilter={setStatementTypeFilter}
                startBalances={startBalances}
                statementTypeFilter={statementTypeFilter}
                transactionOptions={transactionOptions}
            />
            {statementTypeFilter === 'Real' && (
                <PayableFilters
                    onUpdateClientStatus={onUpdateClientStatus}
                    onUpdatePaymentStatus={onUpdatePaymentStatus}
                    onUpdateInvoiceStatus={onUpdateInvoiceStatus}
                />
            )}
            {activeTransactionsWithPayments && activeTransactionsWithPayments.length > 0 && (
                <PayableTools
                    onUpdateExpand={onUpdateExpand}
                    statementTypeFilter={statementTypeFilter}
                    handleOpenPrintDrawer={handleOpenPrintDrawer}
                />
            )}
            {statement && loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            )}
            {statement && (
                <PayableBody
                    transactions={activeTransactionsWithPayments}
                    isPrevisional={statementTypeFilter === 'Previsional'}
                    isHold={statementTypeFilter === 'Hold'}
                    statementTotals={StatementTotals}
                    billingInfoColorMap={billingInfoColorMap}
                />
            )}
            {printDrawerVisible && (
                <NwDrawer title="Print Statement" onClose={handleClosePrintDrawer} width="60vw" whiteBg={false}>
                    <PayablePrintable
                        balances={
                            statementTypeFilter === 'Real'
                                ? {
                                      availableBalances: availableBalances,
                                      totalBalances: totalBalances,
                                  }
                                : null
                        }
                        transactions={activeTransactionsWithPayments}
                        onClose={handleClosePrintDrawer}
                        statementTotals={StatementTotals}
                        startBalances={startBalances}
                        endBalances={endBalances}
                    />
                </NwDrawer>
            )}
        </StatementContainer>
    );
};

export default PayableWrapper;
